import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/** Por encima de modales y overlays existentes en la app. */
const TOOLTIP_Z_INDEX = 12000;
const TOOLTIP_WIDTH_PX = 256;

export interface HelpTooltipProps {
  content: React.ReactNode;
  ariaLabel?: string;
  variant?: 'question' | 'info';
  size?: 'sm' | 'md' | 'lg';
  align?: 'start' | 'end';
  className?: string;
  stopPropagation?: boolean;
}

const triggerSizeClasses = {
  sm: 'h-3.5 w-3.5 text-[9px]',
  md: 'h-4 w-4 text-[10px]',
  lg: 'h-5 w-5 text-[11px]',
} as const;

const InfoIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
      clipRule="evenodd"
    />
  </svg>
);

export const HelpTooltip: React.FC<HelpTooltipProps> = ({
  content,
  ariaLabel,
  variant = 'question',
  size = 'md',
  align = 'end',
  className = '',
  stopPropagation = false,
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const label = ariaLabel ?? (typeof content === 'string' ? content : 'Ayuda');
  const isInfo = variant === 'info';
  const isLarge = size === 'lg';

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const viewportPadding = 16;
    const maxWidth = Math.min(TOOLTIP_WIDTH_PX, window.innerWidth - viewportPadding * 2);

    let left = align === 'end' ? rect.right - maxWidth : rect.left;
    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - maxWidth - viewportPadding));

    const estimatedHeight = 160;
    let top = rect.bottom + 6;
    if (top + estimatedHeight > window.innerHeight - viewportPadding) {
      top = Math.max(viewportPadding, rect.top - estimatedHeight - 6);
    }

    setPosition({
      top,
      left,
    });
  }, [align]);

  const show = useCallback(() => {
    updatePosition();
    setIsOpen(true);
  }, [updatePosition]);

  const hide = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();
    const handleReposition = () => updatePosition();
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isOpen, updatePosition]);

  const interactionProps = stopPropagation
    ? {
        onMouseDown: (event: React.MouseEvent) => event.stopPropagation(),
        onClick: (event: React.MouseEvent) => event.stopPropagation(),
      }
    : {};

  const tooltipContent =
    typeof content === 'string' ? (
      <p className="text-left text-[11px] leading-snug text-gray-300">{content}</p>
    ) : (
      content
    );

  return (
    <>
      <span ref={triggerRef} className={`inline-flex shrink-0 ${className}`}>
        <span
          {...interactionProps}
          tabIndex={0}
          role="button"
          className={[
            'inline-flex items-center justify-center rounded-full cursor-help',
            'focus:outline-none focus:ring-2 focus:ring-teal-500',
            triggerSizeClasses[size],
            isLarge
              ? 'bg-white/85 text-slate-600 shadow-sm ring-1 ring-black/10 transition hover:bg-white hover:text-slate-800 hover:ring-black/20 dark:bg-gray-900/55 dark:text-gray-200 dark:ring-white/15 dark:hover:bg-gray-900/75 dark:hover:text-white'
              : isInfo
                ? 'border border-gray-300 text-gray-500 font-bold dark:border-gray-600 dark:text-gray-400'
                : 'bg-gray-200 text-gray-500 font-bold dark:bg-gray-600 dark:text-gray-400',
          ].join(' ')}
          aria-label={label}
          onMouseEnter={show}
          onMouseLeave={hide}
          onFocus={show}
          onBlur={hide}
        >
          {isLarge ? (
            <InfoIcon className="h-3.5 w-3.5" />
          ) : isInfo ? (
            'i'
          ) : (
            '?'
          )}
        </span>
      </span>
      {isOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            role="tooltip"
            style={{ top: position.top, left: position.left, zIndex: TOOLTIP_Z_INDEX }}
            className={[
              'pointer-events-none fixed w-64 max-w-[calc(100vw-2rem)]',
              'rounded-lg border border-gray-600 bg-gray-900 px-3 py-2.5 shadow-xl',
              'animate-in fade-in slide-in-from-top-1 duration-150',
            ].join(' ')}
          >
            {tooltipContent}
          </div>,
          document.body
        )}
    </>
  );
};
